"use client";

import * as React from "react";
import { Label, Pie, PieChart } from "recharts";

import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/lib/shadcn/ui/card";
import { ChartConfig, ChartContainer, ChartLegend, ChartLegendContent, ChartTooltip, ChartTooltipContent } from "@/lib/shadcn/ui/chart";
import { Table, TableCell, TableHead, TableHeader, TableRow } from "@/lib/shadcn/ui/table";
import PercentageChangeText from "@/lib/shadcn/components/percentage-change-text";

export interface DonutChartData {
    group: string;
    value: number;
    fill: string;
    [key: string]: any;
}

export interface DonutChartProps {
    data: DonutChartData[];
    config: ChartConfig;
    metricLabel: string;
    title: string;
    description: string;
    dataPrevious?: DonutChartData[];
    valueFormatter?: (value: number) => string;
}

export function DonutChartWithText(props: DonutChartProps) {
    const total = React.useMemo(() => {
        return props.data.reduce((acc, curr) => acc + curr.value, 0);
    }, [props.data]);

    const valueFormatter = props.valueFormatter === undefined ? (val: number) => val.toFixed(0) : props.valueFormatter;

    return (
        <Card className="flex flex-col w-full">
            <CardHeader className="items-center pb-0">
                <CardTitle>{props.title}</CardTitle>
                <CardDescription>{props.description}</CardDescription>
            </CardHeader>
            <CardContent className="flex-1 pb-0">
                <ChartContainer config={props.config} className="mx-auto aspect-square max-h-[350px] px-3">
                    <PieChart margin={{ top: 40, right: 40, bottom: 40, left: 40 }}>
                        <ChartTooltip
                            cursor={false}
                            content={<ChartTooltipContent hideLabel={false} hideIndicator={false} formatter={(value) => valueFormatter(Number(value))} />}
                        />
                        <Pie
                            data={props.data}
                            dataKey="value"
                            nameKey="group"
                            innerRadius={60}
                            strokeWidth={30}
                            outerRadius={85}
                            label={({ cx, cy, midAngle, outerRadius, value }) => {
                                const RADIAN = Math.PI / 180;
                                // Adjust these values to position the labels further from the pie
                                const radius = outerRadius * 1.2;
                                const x = cx + radius * Math.cos(-(midAngle ?? 0) * RADIAN);
                                const y = cy + radius * Math.sin(-(midAngle ?? 0) * RADIAN);
                                const percentage = ((value / total) * 100).toFixed(1);

                                return (
                                    <>
                                        <text
                                            x={x}
                                            y={y}
                                            className="fill-muted-foreground text-xs"
                                            textAnchor={x > cx ? "start" : "end"}
                                            dominantBaseline="middle"
                                        >
                                            <tspan x={x} dy="-1em">
                                                {valueFormatter(value)}
                                            </tspan>
                                            <tspan x={x} dy="1.2em">
                                                ({percentage}%)
                                            </tspan>
                                        </text>
                                    </>
                                );
                            }}
                            labelLine={{
                                stroke: "var(--muted-foreground)",
                                strokeWidth: 1,
                            }}
                        >
                            <Label
                                content={({ viewBox }) => {
                                    if (viewBox && "cx" in viewBox && "cy" in viewBox) {
                                        return (
                                            <text x={viewBox.cx} y={viewBox.cy} textAnchor="middle" dominantBaseline="middle">
                                                <tspan x={viewBox.cx} y={viewBox.cy} className="fill-foreground text-2xl font-bold">
                                                    {valueFormatter(total)}
                                                </tspan>
                                                <tspan x={viewBox.cx} y={(viewBox.cy || 0) + 24} className="fill-muted-foreground">
                                                    {props.metricLabel}
                                                </tspan>
                                            </text>
                                        );
                                    }
                                }}
                            />
                        </Pie>
                        <ChartLegend
                            verticalAlign="top"
                            align={"center"}
                            layout="horizontal"
                            content={<ChartLegendContent />}
                            wrapperStyle={{ textAlign: "center", bottom: 0 }}
                        />
                    </PieChart>
                </ChartContainer>
            </CardContent>
            {props.dataPrevious && (
                <CardFooter className="flex-col gap-2 text-sm mt-4">
                    <div className="leading-none text-muted-foreground text-center">
                        Compared to Previous Year
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead className={"text-lef"}>Lead Days</TableHead>
                                    <TableHead className={"text-right"}>Value</TableHead>
                                    <TableHead className={"text-right"}>% Change</TableHead>
                                </TableRow>
                                {props.data.map((groupVal) => {
                                    let prevVal = 0;
                                    let prevValPercentage = 0;
                                    let currentVal = groupVal.value;
                                    let currentValPercentage = 0;
                                    if (props.dataPrevious) {
                                        prevVal = props.dataPrevious.find((prevVal) => prevVal.group === groupVal.group)?.value || 0;
                                        currentValPercentage =
                                            currentVal === 0 ? 0 : (currentVal / props.data.reduce((acc, curr) => acc + curr.value, 0)) * 100;
                                        prevValPercentage = prevVal === 0 ? 0 : (prevVal / props.dataPrevious.reduce((acc, curr) => acc + curr.value, 0)) * 100;
                                    }
                                    const percentageChange = prevVal === 0 ? 0 : ((currentValPercentage - prevValPercentage) / prevValPercentage) * 100;
                                    return (
                                        <TableRow key={groupVal.group}>
                                            <TableCell className={"text-left"}>{props.config[groupVal.group].label}</TableCell>
                                            <TableCell className={"text-right"}>{`${valueFormatter(prevVal)} (${Math.round(prevValPercentage)}%)`}</TableCell>
                                            <TableCell className={"text-right"}>
                                                <PercentageChangeText value={percentageChange} valueFormatter={() => Math.round(percentageChange).toString()} />
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableHeader>
                        </Table>
                    </div>
                </CardFooter>
            )}
        </Card>
    );
}
